import { useContext, useEffect, useState } from 'react'
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography, useTheme } from '@mui/material'
import { Clear } from '@mui/icons-material'
import { EbAuthenticatedAppContext } from '@eidsivabredband/app-framework'
import { DocumentUploadModel, EventId } from '../../../interfaces/IEvent'
import DialogCloseButton from '../../../components/Buttons/DialogCloseButton'
import { enqueueSnackbar } from 'notistack'
import { Input } from '../../../components/Buttons/StyledInput'
import InputField from '../../../components/InputFields/InputField'
import { LoadingIndicator } from '@eidsivabredband/react-component-ui-package'

interface DocumentUploadProps {
  eventId: EventId
  isOpen: boolean
  onClose: () => void
  onUpload: () => void
}

function UploadDocument({ eventId, isOpen, onClose, onUpload }: DocumentUploadProps) {
  const { context } = useContext(EbAuthenticatedAppContext)
  const theme = useTheme()
  const [documentUploadModel, setDocumentUploadModel] = useState<DocumentUploadModel>({} as DocumentUploadModel)
  const [isButtonDisabled, setButtonDisabled] = useState(true)
  const [sendLoading, setSendLoading] = useState(false)

  const handleFileSelect = (event: any) => {
    setDocumentUploadModel((prevState) => ({
      ...prevState,
      file: event.target.files[0],
      fileName: event.target.files[0].name,
    }))
  }

  const handleClearFileSelect = () => {
    setDocumentUploadModel((prevState) => ({
      ...prevState,
      fileName: '',
      file: null,
    }))
  }

  const handleCommentChange = (event: any) => {
    setDocumentUploadModel((prevState) => ({
      ...prevState,
      comment: event.target.value,
    }))
  }

  const handleClearComment = () => {
    setDocumentUploadModel((prevState) => ({
      ...prevState,
      comment: '',
    }))
  }

  const handleCloseDocumentUpload = () => {
    onClose()
    handleClearFileSelect()
    handleClearComment()
  }

  const handleUploadDocument = async (event: any) => {
    setSendLoading(true)

    event.preventDefault()

    const formData = new FormData()
    if (documentUploadModel.file) {
      formData?.append('file', documentUploadModel.file)
    }
    formData?.append('comment', documentUploadModel.comment ?? '')

    var response = await context.restApiClient.buildRequest(`/api/${eventId}/upload`).addFormData(formData).postAsync()

    if (response.error) {
      enqueueSnackbar(<Typography variant="body2">Kunne ikke laste opp dokumentet. Prøv igjen eller kontakt kundeservice</Typography>, {
        variant: 'error',
      })
    } else {
      enqueueSnackbar(<Typography variant="body2">Dokumentet ble lagret.</Typography>, { variant: 'success' })
      onUpload()
      handleClearFileSelect()
      handleClearComment()
    }

    setSendLoading(false)
  }

  useEffect(() => {
    documentUploadModel?.file ? setButtonDisabled(false) : setButtonDisabled(true)
  }, [documentUploadModel.file])

  return (
    <Dialog data-testid="doc-dialog" open={isOpen} fullWidth maxWidth="sm">
      <DialogTitle>
        Dokumentlagring
        <DialogCloseButton handleClose={handleCloseDocumentUpload} />
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleUploadDocument}>
          {!documentUploadModel.file && (
            <Box sx={{
              mt: 2
            }}>
              <label htmlFor="contained-button-file">
                <Input data-testid="button-file" id="contained-button-file" onChange={handleFileSelect} type="file" />
                <Button variant="contained" size="small" component="span" color="primary" fullWidth>
                  Velg dokument
                </Button>
              </label>
            </Box>
          )}
          {documentUploadModel.file && (
            <Box sx={{
              mt: 2
            }}>
              <TextField
                required
                size="small"
                variant="standard"
                label="Dokument"
                fullWidth
                value={`${documentUploadModel.file.name}`}
                slotProps={{
                  input: {
                    endAdornment: (
                      <IconButton size="small" onClick={handleClearFileSelect}>
                        <Clear fontSize="small" />
                      </IconButton>
                    ),
                  },

                  htmlInput: {
                    style: {
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: 16,
                    },
                  }
                }} />
            </Box>
          )}
          <Box sx={{
            mt: 2
          }}>
            <InputField name="comment" title="Alernativ filnavn / beskrivelse" value={documentUploadModel.comment ?? ''} onChange={handleCommentChange} maxLength={70} />
          </Box>
          <Box sx={{
            mt: 2
          }}>
            <label htmlFor="contained-button-upload">
              <Input data-testid="button-upload" id="contained-button-upload" disabled={isButtonDisabled} type="submit" />
              <Button
                disabled={isButtonDisabled || sendLoading}
                startIcon={sendLoading ? <LoadingIndicator size={'xsmall'} /> : null}
                variant="contained"
                size="small"
                component="span"
                fullWidth
              >
                Last opp
              </Button>
            </label>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default UploadDocument
